import { useEffect, useState } from 'react'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui'

import { useFestivalLanguages } from './useFestivalLanguages'

interface TranslationLike {
  locale: string
  id?: string
}

interface TranslationTabsProps<T extends TranslationLike> {
  /** Current translations (from the loaded DTO, or []). Controlled. */
  value: T[]
  /** Replace-all: emits the full set of non-empty locale entries on every change. */
  onChange: (next: T[]) => void
  /** Build a blank entry for a locale, e.g. `(locale) => ({ locale, title: '' })`. */
  makeEmpty: (locale: string) => T
  /** Per-locale field editor. */
  renderFields: (args: {
    value: T
    locale: string
    onFieldChange: (patch: Partial<T>) => void
  }) => JSX.Element
}

/** An entry has content if any field other than `locale`/`id` is a non-blank string. */
const hasContent = (entry: TranslationLike): boolean =>
  Object.entries(entry).some(
    ([key, val]) =>
      key !== 'locale' &&
      key !== 'id' &&
      typeof val === 'string' &&
      val.trim() !== '',
  )

/**
 * Locale-tab editor driven by the festival's configured languages. Preserves each
 * touched translation's `id` (so the backend updates rather than recreates) and
 * always emits the complete set of non-empty locales (replace-all semantics).
 */
export function TranslationTabs<T extends TranslationLike>({
  value,
  onChange,
  makeEmpty,
  renderFields,
}: TranslationTabsProps<T>) {
  const { languages, defaultLocale } = useFestivalLanguages()
  const [active, setActive] = useState<string>(defaultLocale)

  useEffect(() => {
    setActive((prev) =>
      languages.some((l) => l.locale === prev) ? prev : defaultLocale,
    )
  }, [defaultLocale, languages])

  const entryFor = (locale: string): T =>
    value.find((v) => v.locale === locale) ?? makeEmpty(locale)

  const handleFieldChange = (locale: string, patch: Partial<T>) => {
    const updated: T = { ...entryFor(locale), ...patch, locale }
    // Union of festival locales and any pre-existing locales, so untouched
    // translations are never silently dropped.
    const locales = Array.from(
      new Set([
        ...languages.map((l) => l.locale),
        ...value.map((v) => v.locale),
      ]),
    )
    const next = locales
      .map((loc) =>
        loc === locale ? updated : value.find((v) => v.locale === loc),
      )
      .filter((e): e is T => Boolean(e))
      .filter(hasContent)
    onChange(next)
  }

  if (languages.length === 0) {
    return (
      <div className="text-xs text-monochrome-2">
        No festival languages configured. Add languages in Settings → Languages
        first.
      </div>
    )
  }

  return (
    <Tabs value={active} onValueChange={setActive} className="w-full">
      <TabsList className="flex-wrap">
        {languages.map((l) => (
          <TabsTrigger key={l.locale} value={l.locale}>
            {l.name || l.locale}
          </TabsTrigger>
        ))}
      </TabsList>
      {languages.map((l) => (
        <TabsContent key={l.locale} value={l.locale} className="pt-4">
          {renderFields({
            value: entryFor(l.locale),
            locale: l.locale,
            onFieldChange: (patch) => handleFieldChange(l.locale, patch),
          })}
        </TabsContent>
      ))}
    </Tabs>
  )
}
